import {
	ensureNotNil
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { isAndroid } from "client_legacy/ndaw/mobile";
import {
	ChangeEvent,
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef
} from "react";
import { useDropzoneContext } from "client/Contexts/DropzoneContext";
import {
	extractFileSelectionEntriesFromFileList,
	FileSelectionEntry,
	FolderTraversal
} from "client_legacy/FileUploader/utils";
import {
	AlertManager,
	useAlerts,
	useForceRerender
} from "client/Hooks";
import styles from "./DocumentSelect.module.less";

export type DocumentSelectHandle = {
	selectFiles: () => void;
	selectFolder: () => void;
};

export type DocumentSelectProps = {
	onDocumentSelect: (files: readonly FileSelectionEntry[]) => void;
};

export const DocumentSelect = forwardRef(
	(
		props: DocumentSelectProps,
		ref: Ref<DocumentSelectHandle>
	): ReactElement => {
		const {
			onDocumentSelect
		} = props;

		const [resetInputs, version] = useForceRerender();
		const selectFilesButtonRef: RefObject<HTMLInputElement | null> = useRef<HTMLInputElement>(null);
		const selectFoldersButtonRef: RefObject<HTMLInputElement | null> = useRef<HTMLInputElement>(null);

		const { register: registerDropHandler } = useDropzoneContext();
		const alertManager: AlertManager = useAlerts();

		const fileOrFolderSelectedHandler = useCallback(
			(args: ChangeEvent<HTMLInputElement>): void => {
				const input: HTMLInputElement = args.target;
				const entries: readonly FileSelectionEntry[] = extractFileSelectionEntriesFromFileList(input.files);

				onDocumentSelect(entries);
				resetInputs();
			},
			[
				onDocumentSelect,
				resetInputs
			]
		);

		useImperativeHandle(
			ref,
			(): DocumentSelectHandle => ({
				selectFiles: (): void => {
					ensureNotNil(selectFilesButtonRef.current)
						.click();
				},
				selectFolder: (): void => {
					ensureNotNil(selectFoldersButtonRef.current)
						.click();
				}
			}),
			[]
		);

		useEffect(
			(): (() => void) => {
				return registerDropHandler(
					["Files"],
					async (args): Promise<boolean> => {
						const uploadEntries: readonly FileSelectionEntry[] = await args
							.getFiles(FolderTraversal.DeepWithoutDirectories);

						if (uploadEntries.length === 0) {
							alertManager.showError(localize("_ThisBrowserDoesNotSupportFolderUploadsOrFolderIsEmpty"));
						} else {
							onDocumentSelect(uploadEntries);
						}

						return true;
					}
				);
			},
			[
				alertManager,
				registerDropHandler,
				onDocumentSelect
			]
		);

		return (
			<>
				<input
					key={`file-input-${version}`}
					type="file"
					multiple
					onChange={fileOrFolderSelectedHandler}
					ref={selectFilesButtonRef}
					className={styles.fileInput}
				/>

				<input
					key={`folder-input-${version}`}
					type="file"
					multiple
					onChange={fileOrFolderSelectedHandler}
					ref={selectFoldersButtonRef}
					className={styles.fileInput}
					/* @ts-expect-error */
					webkitdirectory=""
					// eslint-disable-next-line react/no-unknown-property
					directory=""
				/>
			</>
		);
	}
);
DocumentSelect.displayName = "DocumentSelect";

// On android (stock and chrome) both report it supports folder selection though actually it doesn't
// (https://bugs.chromium.org/p/chromium/issues/detail?id=465190).
export const doesBrowserSupportsFolderSelection: boolean = document.createElement("input")
	.webkitdirectory !== undefined
	&& !isAndroid();
